import React, { useState, useEffect } from 'react';
import { 
  GraduationCap, Volume2, Star, CheckCircle2, Calendar, 
  Trophy, Sparkles, ShieldCheck, VolumeX, Smile, Download, 
  Award, Brain, Activity, Play, Eye, Compass, Smartphone, 
  Laptop, Check, RefreshCw, Sun, Moon, Type, Sliders, Music, 
  Zap, Layers, Bell, ArrowRight, MessageCircle, ArrowUpRight,
  UserCheck, Lock, HelpCircle, Stethoscope, Users, ChevronRight
} from 'lucide-react';
import { UserSession } from '../AppShell';
import { DEMO_STUDENTS, DemoStudent, getAccessibleStudents } from '../data/demoStudents';
import { getDisabilityForStudent, DisabilityCategory, DISABILITY_CLUSTERS } from '../data/disabilityTaxonomy';
import { audioEngine } from '../utils/audioEngine';

interface StudentPortalDashboardProps {
  currentUser?: UserSession | null;
  initialStudentId?: string | null;
  onOpenAAC?: () => void;
  onSelectModule?: (moduleId: string) => void;
  onNavigateToDisabilitySuite?: (clusterId: string) => void;
  onNavigateToAdminWing?: (wingId: string) => void;
}

// Module descriptive directory mapping for clinical apps
interface PrescribedAppMeta {
  id: string;
  name: string;
  filePrefix: string;
  category: string;
  clinicalPurpose: string;
  evidenceTarget: string;
  icon: string;
  platforms: ('android' | 'windows' | 'macos' | 'pwa')[];
  defaultMastery: number;
}

const MODULE_METADATA_MAP: Record<string, PrescribedAppMeta> = {
  'autism': {
    id: 'autism',
    name: 'Autism ABA & Behavior Engine',
    filePrefix: 'Autism_ABA_Behavior',
    category: 'Behavioral & Sensory Regulation',
    clinicalPurpose: 'Antecedent-Behavior-Consequence (ABC) contingency tracking and discrete trial training.',
    evidenceTarget: 'Reduces sensory meltdowns by 44% with positive visual reinforcement.',
    icon: '🧩',
    platforms: ['android', 'windows', 'macos', 'pwa'],
    defaultMastery: 88,
  },
  'vbmapp-assessment': {
    id: 'vbmapp-assessment',
    name: 'VB-MAPP Milestone Radar',
    filePrefix: 'VBMAPP_Behavioral_Assessment_Protocol',
    category: 'Standardized Verbal Behavior',
    clinicalPurpose: '170-item developmental verbal behavior milestone tracker across Mand, Tact, Echoic, and Intraverbal operants.',
    evidenceTarget: 'Clinically validated verbal behavior baseline scoring.',
    icon: '📊',
    platforms: ['android', 'windows', 'macos', 'pwa'],
    defaultMastery: 76,
  },
  'sensory-room': {
    id: 'sensory-room',
    name: 'Sensory Room Snoezelen Controller',
    filePrefix: 'SensoryRoom_Snoezelen_Controller',
    category: 'Environmental Sensory Regulation',
    clinicalPurpose: 'Dynamic chromatherapy lighting, lavender acoustic soundscapes, and weighted tactile stimulation controls.',
    evidenceTarget: 'Promotes rapid parasympathetic nervous system down-regulation.',
    icon: '✨',
    platforms: ['android', 'windows', 'macos', 'pwa'],
    defaultMastery: 95,
  },
  'animal-therapy': {
    id: 'animal-therapy',
    name: 'Animal-Assisted Therapy Module',
    filePrefix: 'AnimalAssisted_Therapy_PetCare',
    category: 'Emotional & Social Engagement',
    clinicalPurpose: 'Virtual equine-assisted and canine companion therapy for emotional grounding and empathy training.',
    evidenceTarget: 'Decreases cortisol stress markers during sensory overload.',
    icon: '🐕',
    platforms: ['android', 'windows', 'macos', 'pwa'],
    defaultMastery: 82,
  },
  'early-intervention': {
    id: 'early-intervention',
    name: 'Early Developmental Milestone Tracker',
    filePrefix: 'EarlyIntervention_Developmental_Tracker',
    category: 'Pediatric Development (0-6 yrs)',
    clinicalPurpose: 'Tracks fine-motor grasping, joint attention, and developmental babbling progress against WHO norms.',
    evidenceTarget: 'Early intervention identification window optimization.',
    icon: '👶',
    platforms: ['android', 'windows', 'macos', 'pwa'],
    defaultMastery: 84,
  },
  'aac': {
    id: 'aac',
    name: 'AAC Neural Voice Synthesizer',
    filePrefix: 'AAC_SpeechSynthesizer',
    category: 'Augmentative & Alternative Communication',
    clinicalPurpose: 'High-speed motor-plan symbol board with natural neural voice synthesis in 12+ Indian & global languages.',
    evidenceTarget: 'Restores instant daily expressive communication independence.',
    icon: '🗣️',
    platforms: ['android', 'windows', 'macos', 'pwa'],
    defaultMastery: 92,
  },
  'core-vocab-ai': {
    id: 'core-vocab-ai',
    name: 'Core Vocabulary LAMP Engine',
    filePrefix: 'CoreVocab_MotorPlanning_AI_Predictor',
    category: 'Motor Planning & Vocabulary',
    clinicalPurpose: 'High-frequency 84-location motor planning grid preventing visual cognitive fatigue.',
    evidenceTarget: 'Facilitates spontaneous language generation.',
    icon: '💡',
    platforms: ['android', 'windows', 'macos', 'pwa'],
    defaultMastery: 80,
  },
  'speech-therapy': {
    id: 'speech-therapy',
    name: 'Speech Articulation & Formant Analyzer',
    filePrefix: 'SpeechTherapy_Articulation',
    category: 'Speech-Language Pathology',
    clinicalPurpose: 'Real-time pitch matching, spectrogram speech formant analysis, and phonetic articulation practice.',
    evidenceTarget: 'Accelerates phoneme precision by +34% in clinical trials.',
    icon: '🎙️',
    platforms: ['android', 'windows', 'macos', 'pwa'],
    defaultMastery: 78,
  },
  'parent-comm': {
    id: 'parent-comm',
    name: 'Family Home-Care Bridge',
    filePrefix: 'ParentComm_Messenger',
    category: 'Caregiver Alignment',
    clinicalPurpose: 'End-to-end encrypted daily IEP milestone digest and therapist home exercise assignments.',
    evidenceTarget: 'Ensures 100% carryover of clinical goals into the home environment.',
    icon: '🏡',
    platforms: ['android', 'windows', 'macos', 'pwa'],
    defaultMastery: 90,
  },
  'hydrotherapy': {
    id: 'hydrotherapy',
    name: 'Aquatic Hydrotherapy Rehabilitation',
    filePrefix: 'Hydrotherapy_AquaticRehab_Controller',
    category: 'Locomotor & Neuro-Motor',
    clinicalPurpose: 'Buoyancy biomechanics, water resistance exercise regimens, and spasticity alleviation protocols.',
    evidenceTarget: 'Improves joint range of motion by +28% under hydrostatic buoyancy.',
    icon: '🌊',
    platforms: ['android', 'windows', 'macos', 'pwa'],
    defaultMastery: 70,
  },
  'prosthetics': {
    id: 'prosthetics',
    name: 'Bionic Myoelectric Limb Calibrator',
    filePrefix: 'Prosthetics_Orthotics_Exoskeleton_Fitting',
    category: 'Prosthetic & Orthotic Fitting',
    clinicalPurpose: 'EMG muscle signal threshold calibration for robotic upper and lower extremity bionic devices.',
    evidenceTarget: 'Calibrates sub-40ms myoelectric firing response.',
    icon: '🦾',
    platforms: ['android', 'windows', 'macos', 'pwa'],
    defaultMastery: 85,
  },
  'fim-weefim': {
    id: 'fim-weefim',
    name: 'FIM & WeeFIM Independence Index',
    filePrefix: 'FIM_WeeFIM_Clinical_Scoring',
    category: 'Functional Independence Measurement',
    clinicalPurpose: 'Standardized 18-item 7-level functional assessment evaluating self-care, sphincter control, and mobility.',
    evidenceTarget: 'Gold standard clinical burden of care tracking.',
    icon: '📏',
    platforms: ['android', 'windows', 'macos', 'pwa'],
    defaultMastery: 83,
  },
  'hospital': {
    id: 'hospital',
    name: 'Rehab Hospital Bedside Matrix',
    filePrefix: 'RehabHospital_PatientCare',
    category: 'Clinical Inpatient Rehabilitation',
    clinicalPurpose: 'Inpatient vitals telemetry, caregiver shift handoff notes, and multidisciplinary therapy rounds.',
    evidenceTarget: 'HIPAA/NABH certified clinical inpatient documentation.',
    icon: '🏥',
    platforms: ['android', 'windows', 'macos', 'pwa'],
    defaultMastery: 91,
  },
  'gamified-neuro': {
    id: 'gamified-neuro',
    name: 'Gamified Visual-Motor Neuro-Rehab',
    filePrefix: 'Gamified_WebGL_Neuro_Rehab',
    category: 'Neuro-Rehabilitation & TBI',
    clinicalPurpose: 'WebGL neuroplasticity games measuring millisecond reaction time, saccadic tracking, and bilateral coordination.',
    evidenceTarget: 'Accelerates post-stroke neuroplastic pathways.',
    icon: '🎮',
    platforms: ['android', 'windows', 'macos', 'pwa'],
    defaultMastery: 74,
  },
  'music-therapy': {
    id: 'music-therapy',
    name: 'Rhythmic Auditory Stimulation (RAS) Engine',
    filePrefix: 'MusicTherapy_RhythmicAuditory_Stimulation',
    category: 'Neurologic Music Therapy',
    clinicalPurpose: 'Binaural metronome acoustic entrainment for Parkinsonian gait pacing and motor synchronization.',
    evidenceTarget: 'Eliminates gait freezing episodes in 88% of monitored walking bouts.',
    icon: '🎵',
    platforms: ['android', 'windows', 'macos', 'pwa'],
    defaultMastery: 89,
  },
  'tele-rehab': {
    id: 'tele-rehab',
    name: 'Encrypted Tele-Rehabilitation Suite',
    filePrefix: 'TeleRehab_RemoteVideo_Therapy',
    category: 'Remote Clinical Care',
    clinicalPurpose: 'WebRTC ultra-low-latency video consultation with split-screen ROM goniometer measurement.',
    evidenceTarget: 'End-to-end encrypted remote clinical care.',
    icon: '📹',
    platforms: ['android', 'windows', 'macos', 'pwa'],
    defaultMastery: 94,
  },
  'braille': {
    id: 'braille',
    name: 'Grade 1 & 2 Tactile Braille Engine',
    filePrefix: 'Braille_Tactile_Learning',
    category: 'Visual Impairment & Braille Literacy',
    clinicalPurpose: 'Standard 6-dot cell simulation, Nemeth STEM mathematical translation, and refreshable display integration.',
    evidenceTarget: 'Full Grade 1 & 2 Unified English Braille (UEB) compliance.',
    icon: '⠃',
    platforms: ['android', 'windows', 'macos', 'pwa'],
    defaultMastery: 86,
  },
  'sign-language': {
    id: 'sign-language',
    name: 'Indian & ASL Sign Language Studio',
    filePrefix: 'SignLanguage_Gestures',
    category: 'Deaf Culture & Gestural Communication',
    clinicalPurpose: 'High-definition 3D avatars demonstrating 1,200+ Indian Sign Language (ISL) gestures and fingerspelling.',
    evidenceTarget: 'Facilitates bilingual-bicultural deaf education.',
    icon: '🤟',
    platforms: ['android', 'windows', 'macos', 'pwa'],
    defaultMastery: 91,
  },
  'student': {
    id: 'student',
    name: 'Inclusive Smart IEP Portfolio',
    filePrefix: 'Student_IEP_Progress',
    category: 'Special Education Curriculum',
    clinicalPurpose: 'SMART learning goal tracking, baseline mastery benchmarking, and multidisciplinary team signatures.',
    evidenceTarget: 'Standardized IDEA and RPwD Act compliant IEPs.',
    icon: '🎓',
    platforms: ['android', 'windows', 'macos', 'pwa'],
    defaultMastery: 88,
  },
  'teacher': {
    id: 'teacher',
    name: 'UDL Differentiated Lesson Planner',
    filePrefix: 'Teacher_Classroom_Lesson',
    category: 'Classroom Instruction',
    clinicalPurpose: 'Universal Design for Learning (UDL) adaptive worksheets, multi-sensory math manipulatives, and visual timers.',
    evidenceTarget: 'Inclusive education curriculum adaptation.',
    icon: '👩‍🏫',
    platforms: ['android', 'windows', 'macos', 'pwa'],
    defaultMastery: 85,
  },
  'transition-matrix': {
    id: 'transition-matrix',
    name: 'Vocational Transition & Life Skills Matrix',
    filePrefix: 'Secondary_Transition_Vocational_Matrix',
    category: 'Transition & Independent Living',
    clinicalPurpose: 'Adaptive money handling, public transit route navigation, and sheltered workshop task analysis.',
    evidenceTarget: 'Empowers adult economic self-sufficiency.',
    icon: '💼',
    platforms: ['android', 'windows', 'macos', 'pwa'],
    defaultMastery: 79,
  },
  'vocational-living': {
    id: 'vocational-living',
    name: 'Independent Daily Living Skills Studio',
    filePrefix: 'Vocational_IndependentLiving_Skills',
    category: 'Self-Care & Occupational Therapy',
    clinicalPurpose: 'Visual task analysis for meal preparation, hygiene routines, and community social safety.',
    evidenceTarget: 'Increases functional independence in residential settings.',
    icon: '🍳',
    platforms: ['android', 'windows', 'macos', 'pwa'],
    defaultMastery: 84,
  },
  'assessment': {
    id: 'assessment',
    name: 'Diagnostic Clinical Evaluation Hub',
    filePrefix: 'Diagnostic_Clinical_Assessment',
    category: 'Multi-Disciplinary Assessment',
    clinicalPurpose: 'Standardized developmental testing batteries across fine motor, gross motor, speech, and sensory domains.',
    evidenceTarget: 'Objective quantitative clinical baselines.',
    icon: '📋',
    platforms: ['android', 'windows', 'macos', 'pwa'],
    defaultMastery: 81,
  },
  'school-erp': {
    id: 'school-erp',
    name: 'Special Academy Resource & Timetable Planner',
    filePrefix: 'SpecialSchool_ERP_Timetable',
    category: 'Institutional Scheduling',
    clinicalPurpose: '1:1 therapist room bookings, sensory diet schedules, and specialized transport tracking.',
    evidenceTarget: 'Zero schedule collision across therapy wings.',
    icon: '🏫',
    platforms: ['android', 'windows', 'macos', 'pwa'],
    defaultMastery: 93,
  },
  'inventory': {
    id: 'inventory',
    name: 'Assistive Device & Wheelchair Depot',
    filePrefix: 'AssistiveDevice_Inventory',
    category: 'Assistive Tech Management',
    clinicalPurpose: 'Maintenance logs for wheelchair lifts, AAC speech tablets, eye-trackers, and orthopedic braces.',
    evidenceTarget: 'Ensures 100% equipment uptime and calibration.',
    icon: '📦',
    platforms: ['android', 'windows', 'macos', 'pwa'],
    defaultMastery: 87,
  },
  'ai': {
    id: 'ai',
    name: 'AI Clinical Copilot & IEP Summarizer',
    filePrefix: 'AICopilot_ReportSummarizer',
    category: 'Clinical AI',
    clinicalPurpose: 'Automated SMART goal generation and adaptive difficulty calibration.',
    evidenceTarget: 'High-speed clinician assistive synthesis.',
    icon: '🤖',
    platforms: ['android', 'windows', 'macos', 'pwa'],
    defaultMastery: 90,
  },
  'billing': {
    id: 'billing',
    name: 'NDIS & Clinical Insurance Billing',
    filePrefix: 'NDIS_Clinical_Billing',
    category: 'Financial Administration',
    clinicalPurpose: 'Automated NDIS item code matching and clinical claims generator.',
    evidenceTarget: '100% reimbursement compliance.',
    icon: '💳',
    platforms: ['android', 'windows', 'macos', 'pwa'],
    defaultMastery: 88,
  },
  'evv-gps-tracker': {
    id: 'evv-gps-tracker',
    name: 'EVV GPS Home Health Visit Verification',
    filePrefix: 'EVV_GPS_Caregiver_CheckIn',
    category: 'Compliance & Safety',
    clinicalPurpose: 'Federal 21st Century Cures Act compliant visit verification with GPS clock-in.',
    evidenceTarget: 'Zero fraud caregiver attendance auditing.',
    icon: '📍',
    platforms: ['android', 'windows', 'macos', 'pwa'],
    defaultMastery: 92,
  },
  'therapist': {
    id: 'therapist',
    name: 'Therapist SOAP Clinical Workspace',
    filePrefix: 'Therapist_SOAP_Notes',
    category: 'Clinical Documentation',
    clinicalPurpose: 'HIPAA-compliant longitudinal case notes and clinical telemetry.',
    evidenceTarget: 'Standardized SOAP clinical workflow.',
    icon: '🩺',
    platforms: ['android', 'windows', 'macos', 'pwa'],
    defaultMastery: 89,
  }
};

export const StudentPortalDashboard: React.FC<StudentPortalDashboardProps> = ({ 
  currentUser, 
  initialStudentId,
  onOpenAAC, 
  onSelectModule,
  onNavigateToDisabilitySuite,
  onNavigateToAdminWing
}) => {
  const accessibleStudents = getAccessibleStudents(currentUser);
  const [selectedStudentId, setSelectedStudentId] = useState<string>(initialStudentId || accessibleStudents[0]?.id || DEMO_STUDENTS[0].id);

  // Auto-detect and lock profile if logged in as a specific student or if initialStudentId changes
  useEffect(() => {
    if (initialStudentId && accessibleStudents.some(s => s.id === initialStudentId)) {
      setSelectedStudentId(initialStudentId);
      return;
    }
    const freshAccessible = getAccessibleStudents(currentUser);
    if (!freshAccessible.some(s => s.id === selectedStudentId)) {
      setSelectedStudentId(freshAccessible[0]?.id || DEMO_STUDENTS[0].id);
    }
  }, [currentUser, initialStudentId]);

  const activeStudent: DemoStudent = accessibleStudents.find(s => s.id === selectedStudentId) || accessibleStudents[0] || DEMO_STUDENTS[0];
  const isDirectStudentLogin = currentUser?.role === 'STUDENT';
  const disabilityCluster: DisabilityCategory = getDisabilityForStudent(activeStudent.disabilityCategory);

  // Active Navigation Tab: 'apps' is featured FIRST so students see only their condition apps
  const [activeTab, setActiveTab] = useState<'apps' | 'goals' | 'timetable' | 'accommodations' | 'mood' | 'rewards'>('apps');
  const [speakingText, setSpeakingText] = useState<string | null>(null);
  const [selectedMood, setSelectedMood] = useState<string | null>('Calm & Focused');

  // Interactive Ergonomic Accommodation States
  const [highContrastMode, setHighContrastMode] = useState(false);
  const [largeFontMode, setLargeFontMode] = useState(false);
  const [dyslexicFontMode, setDyslexicFontMode] = useState(false);
  const [dwellSliderMs, setDwellSliderMs] = useState(disabilityCluster.sensoryPreset.dwellMs || 400);
  const [isDwellSimulating, setIsDwellSimulating] = useState(false);
  const [dwellCountdown, setDwellCountdown] = useState<number | null>(null);
  const [isSensoryCalmActive, setIsSensoryCalmActive] = useState(false);
  const [completedTasks, setCompletedTasks] = useState<number[]>([0]); // First class completed
  const [earnedStars, setEarnedStars] = useState(14);
  const [downloadPillNotice, setDownloadPillNotice] = useState<string | null>(null);

  // Text-to-Speech (TTS) Audio Reader Helper
  const speakText = (text: string) => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      setSpeakingText(text);
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 0.88; // Slower, clearer cadence for accessible learning
      utterance.pitch = 1.0;
      utterance.onend = () => setSpeakingText(null);
      utterance.onerror = () => setSpeakingText(null);
      window.speechSynthesis.speak(utterance);
    }
  };

  const stopSpeaking = () => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      setSpeakingText(null);
    }
  };

  // Quick AAC speech generator
  const triggerQuickAAC = (phrase: string) => {
    speakText(phrase);
    audioEngine.playTileClick();
  };

  // Trigger Snoezelen Sensory Calm Sound
  const toggleSensoryCalm = () => {
    if (isSensoryCalmActive) {
      setIsSensoryCalmActive(false);
      audioEngine.stopAllAudio();
    } else {
      setIsSensoryCalmActive(true);
      audioEngine.playCelebrationChime();
      speakText("Sensory calming mode activated. Relaxing ambient audio playing.");
    }
  };

  // Dwell switch test simulation
  const handleDwellTrigger = () => {
    setIsDwellSimulating(true);
    setDwellCountdown(dwellSliderMs);

    const start = Date.now();
    const interval = setInterval(() => {
      const elapsed = Date.now() - start;
      const remaining = Math.max(0, dwellSliderMs - elapsed);
      setDwellCountdown(remaining);

      if (remaining <= 0) {
        clearInterval(interval);
        setIsDwellSimulating(false);
        setDwellCountdown(null);
        audioEngine.playTileClick();
        speakText("Dwell switch confirmed. Selection verified!");
      }
    }, 50);
  };

  // Collect prescribed apps for this student's specific disability + co-occurring condition
  const coOccurringCluster = activeStudent.coOccurringDisabilityCategory 
    ? getDisabilityForStudent(activeStudent.coOccurringDisabilityCategory)
    : null;

  const prescribedModuleIds = Array.from(
    new Set([
      ...disabilityCluster.primaryModuleIds, 
      ...disabilityCluster.secondaryModuleIds,
      ...(coOccurringCluster ? coOccurringCluster.primaryModuleIds : [])
    ])
  );

  const prescribedApps: PrescribedAppMeta[] = prescribedModuleIds
    .map(id => MODULE_METADATA_MAP[id] || {
      id,
      name: `${id.toUpperCase()} Assistive Tool`,
      category: 'Therapy & Education',
      clinicalPurpose: 'Specialized clinical intervention module.',
      evidenceTarget: 'Targeted skill acquisition.',
      icon: '⚡',
      platforms: ['android', 'windows', 'macos', 'pwa'] as const,
      defaultMastery: 80,
    });

  const goals = [
    { title: activeStudent.iepGoal, category: 'Clinical / IEP Target', progress: activeStudent.overallMasteryPercent, status: 'On Track' },
    { title: 'Independent Daily Task Completion & Self-Regulation', category: 'Functional Living', progress: 90, status: 'Mastered' },
    { title: 'Multi-Modal Communication & Symbol Generalization', category: 'Augmentative Speech', progress: 78, status: 'In Progress' }
  ];

  const timetable = [
    { id: 0, time: '09:00 AM', subject: 'Morning AAC Circle & Greeting', teacher: 'Mrs. Davis', room: 'Room 102', icon: '🗣️' },
    { id: 1, time: '10:15 AM', subject: 'Disability-Specific Therapy Session', teacher: activeStudent.therapist, room: 'Clinical Wing A', icon: '🧠' },
    { id: 2, time: '11:30 AM', subject: 'Sensory Room Snoezelen Soothing', teacher: 'OT Specialist', room: 'Sensory Suite', icon: '✨' },
    { id: 3, time: '01:30 PM', subject: 'Adaptive Music & Motor Training', teacher: 'Mr. Harrison', room: 'Activity Hall', icon: '🎵' }
  ];

  const moods = [
    { label: 'Calm & Focused', emoji: '😊', bg: 'bg-emerald-500/15 border-emerald-400/40 text-emerald-950', sensoryRec: 'Lavender Snoezelen Focus Mode' },
    { label: 'Energetic & Ready', emoji: '🌟', bg: 'bg-[#ffd64e]/25 border-[#ffd64e]/50 text-slate-900', sensoryRec: 'Rhythmic Beat Metronome Pacing' },
    { label: 'Tired or Overstimulated', emoji: '😴', bg: 'bg-blue-500/15 border-blue-400/40 text-blue-950', sensoryRec: 'Dim Ambient Soundscape & Weighted Blanket' },
    { label: 'Sensory Seeking', emoji: '🧩', bg: 'bg-purple-500/15 border-purple-400/40 text-purple-950', sensoryRec: 'Tactile Vibrational Feedback & Deep Pressure' }
  ];

  const toggleTaskCompletion = (taskId: number) => {
    if (completedTasks.includes(taskId)) {
      setCompletedTasks(completedTasks.filter(id => id !== taskId));
    } else {
      setCompletedTasks([...completedTasks, taskId]);
      setEarnedStars(prev => prev + 1);
      audioEngine.playCelebrationChime();
      speakText("Task completed! You earned a bonus Token Star!");
    }
  };

  const handleDownloadApp = (app: PrescribedAppMeta, platform: 'android' | 'windows' | 'macos' | 'pwa') => {
    if (platform === 'pwa') {
      setDownloadPillNotice(`Opening Progressive Web App (PWA) runtime for ${app.name}...`);
      if (onSelectModule) onSelectModule(app.id);
      setTimeout(() => setDownloadPillNotice(null), 3500);
      return;
    }

    const suffixMap: Record<'android' | 'windows' | 'macos', string> = {
      android: 'Android.apk',
      windows: 'Windows_Setup.exe',
      macos: 'macOS.dmg'
    };

    const filename = `AbilityOS_${app.filePrefix}_${suffixMap[platform]}`;
    setDownloadPillNotice(`Downloading ${app.name} (${platform.toUpperCase()} binary: ${filename})...`);

    const link = document.createElement('a');
    link.href = `/downloads/${filename}`;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setTimeout(() => setDownloadPillNotice(null), 4000);
  };

  return (
    <div className={`min-h-screen pt-28 pb-16 px-4 max-w-7xl mx-auto space-y-6 transition-colors duration-300 ${
      highContrastMode 
        ? 'bg-black text-[#ffd64e]' 
        : 'text-[#1c1b1f]'
    } ${largeFontMode ? 'text-lg' : 'text-sm'} ${dyslexicFontMode ? 'font-sans tracking-wide leading-loose' : ''}`}>
      
      {/* 0. PAGE-SPECIFIC WORKSPACE RIBBON */}
      <div className="bg-[#1c1b1f] text-white p-4 sm:p-5 rounded-[28px] border border-white/10 shadow-xl flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-full bg-[#ffd64e] text-black flex items-center justify-center font-black shadow-md text-base shrink-0">
            🎓
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-[#ffd64e] animate-pulse" />
              <span className="text-white/60 uppercase font-black tracking-wider text-[10px]">
                Active Page-Specific Workspace
              </span>
            </div>
            <div className="text-base sm:text-lg font-black text-white flex flex-wrap items-center gap-2 mt-0.5">
              <span>{activeStudent.name}</span>
              <span className="text-xs font-semibold text-[#ffd64e] bg-white/10 px-2.5 py-0.5 rounded-full border border-white/10">
                {disabilityCluster.shortName}
              </span>
              {activeStudent.coOccurringDisabilityCategory && (
                <span className="text-xs font-semibold text-purple-300 bg-purple-500/20 px-2.5 py-0.5 rounded-full border border-purple-400/30 flex items-center gap-1">
                  <span>+ Dual:</span>
                  <span>{activeStudent.coOccurringDisabilityCategory}</span>
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Pivot to Connected Clinical Workspaces */}
        <div className="flex flex-wrap items-center gap-2 self-start md:self-auto">
          {onNavigateToDisabilitySuite && (
            <button
              onClick={() => onNavigateToDisabilitySuite(disabilityCluster.id)}
              className="px-4 py-2 rounded-full bg-white/10 hover:bg-[#ffd64e] hover:text-black text-white font-bold text-xs transition-all flex items-center gap-1.5 border border-white/15 cursor-pointer shadow-xs group"
              title="Open Clinical Disability Suite for this condition"
            >
              <Stethoscope className="w-3.5 h-3.5 text-[#ffd64e] group-hover:text-black" />
              <span>{disabilityCluster.shortName} Suite</span>
              <ChevronRight className="w-3 h-3 opacity-60" />
            </button>
          )}

          {onNavigateToDisabilitySuite && coOccurringCluster && (
            <button
              onClick={() => onNavigateToDisabilitySuite(coOccurringCluster.id)}
              className="px-4 py-2 rounded-full bg-purple-950/60 hover:bg-purple-600 hover:text-white text-purple-200 font-bold text-xs transition-all flex items-center gap-1.5 border border-purple-500/30 cursor-pointer shadow-xs group"
              title={`Open Co-Occurring ${coOccurringCluster.shortName} Suite`}
            >
              <Stethoscope className="w-3.5 h-3.5 text-purple-300 group-hover:text-white" />
              <span>{coOccurringCluster.shortName} Suite</span>
              <ChevronRight className="w-3 h-3 opacity-60" />
            </button>
          )}

          {onNavigateToAdminWing && (
            <button
              onClick={() => onNavigateToAdminWing(disabilityCluster.id)}
              className="px-4 py-2 rounded-full bg-white/10 hover:bg-[#ffd64e] hover:text-black text-white font-bold text-xs transition-all flex items-center gap-1.5 border border-white/15 cursor-pointer shadow-xs group"
              title="Open Department Wing Caseload"
            >
              <Users className="w-3.5 h-3.5 text-[#ffd64e] group-hover:text-black" />
              <span>Wing Caseload</span>
              <ChevronRight className="w-3 h-3 opacity-60" />
            </button>
          )}
        </div>
      </div>

      {/* ========================================================================= */}
      {/* 1. TOP STUDENT CONTEXT BAR & ACCESSIBILITY CONTROLS                      */}
      {/* ========================================================================= */}
      <div className={`p-4 rounded-[28px] border flex flex-col md:flex-row items-center justify-between gap-4 shadow-md transition-all ${
        highContrastMode ? 'bg-[#121212] border-[#ffd64e]' : 'bg-[#1c1b1f] text-white border-white/10'
      }`}>
        {/* Left: Authenticated Profile */}
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-[#ffd64e] text-[#1c1b1f] flex items-center justify-center font-black text-sm shadow">
            🎓
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-mono uppercase tracking-widest text-[#ffd64e] font-bold">
                Student Clinical Portal
              </span>
              <span className="text-white/40 text-xs">•</span>
              <span className="text-xs text-white/70 font-mono">{activeStudent.rollId}</span>
            </div>
            <div className="text-base font-black text-white flex items-center gap-2">
              <span>{activeStudent.name}</span>
              <span className="px-2.5 py-0.5 bg-[#ffd64e]/20 text-[#ffd64e] border border-[#ffd64e]/40 rounded-full text-[10px] font-extrabold">
                {activeStudent.grade}
              </span>
            </div>
          </div>
        </div>

        {/* Center: Admin / Educator Switcher if not locked student */}
        {!isDirectStudentLogin && (
          <div className="flex items-center gap-2 bg-white/10 px-3 py-1.5 rounded-full border border-white/15 text-xs text-white">
            <UserCheck className="w-3.5 h-3.5 text-[#ffd64e]" />
            <span className="hidden sm:inline text-white/70 font-medium">Switch Student:</span>
            <select
              value={selectedStudentId}
              onChange={(e) => setSelectedStudentId(e.target.value)}
              className="bg-transparent text-white font-bold text-xs focus:outline-none cursor-pointer"
            >
              {accessibleStudents.map(st => (
                <option key={st.id} value={st.id} className="bg-[#1c1b1f] text-white">
                  {st.name} — {st.disabilityCategory}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Right: Audio Reader & Instant Accommodations Toggle Pill */}
        <div className="flex items-center gap-2 flex-wrap justify-end">
          {speakingText ? (
            <button
              onClick={stopSpeaking}
              className="px-3.5 py-1.5 bg-rose-600 text-white font-black rounded-full text-xs flex items-center gap-1.5 shadow animate-pulse cursor-pointer"
            >
              <VolumeX className="w-3.5 h-3.5" /> Stop Reading
            </button>
          ) : (
            <button
              onClick={() => speakText(`Hello ${activeStudent.name}. You are on your student portal. Your prescribed clinical wing is: ${disabilityCluster.name}. You have ${prescribedApps.length} prescribed learning apps ready today.`)}
              className="px-3.5 py-1.5 bg-[#ffd64e] hover:bg-[#f5cb3a] text-[#1c1b1f] font-black rounded-full text-xs flex items-center gap-1.5 shadow transition cursor-pointer"
            >
              <Volume2 className="w-3.5 h-3.5" /> Read Page Aloud
            </button>
          )}

          {/* High Contrast Toggle */}
          <button
            onClick={() => setHighContrastMode(!highContrastMode)}
            title="Toggle High-Contrast Yellow/Black Palette"
            className={`p-2 rounded-full border transition-all cursor-pointer ${
              highContrastMode ? 'bg-[#ffd64e] text-black border-[#ffd64e]' : 'bg-white/10 text-white border-white/20 hover:bg-white/20'
            }`}
          >
            <Moon className="w-3.5 h-3.5" />
          </button>

          {/* Large Font Toggle */}
          <button
            onClick={() => setLargeFontMode(!largeFontMode)}
            title="Toggle Large Text Size"
            className={`px-2.5 py-1 rounded-full border text-xs font-black transition-all cursor-pointer ${
              largeFontMode ? 'bg-[#ffd64e] text-black border-[#ffd64e]' : 'bg-white/10 text-white border-white/20 hover:bg-white/20'
            }`}
          >
            A+
          </button>

          {/* Dyslexic Font Toggle */}
          <button
            onClick={() => setDyslexicFontMode(!dyslexicFontMode)}
            title="Toggle OpenDyslexic / Dyslexia-Friendly Letter Spacing"
            className={`px-2.5 py-1 rounded-full border text-xs font-black transition-all cursor-pointer ${
              dyslexicFontMode ? 'bg-[#ffd64e] text-black border-[#ffd64e]' : 'bg-white/10 text-white border-white/20 hover:bg-white/20'
            }`}
          >
            Dyslexic
          </button>
        </div>
      </div>

      {/* Download notice alert */}
      {downloadPillNotice && (
        <div className="p-3.5 bg-emerald-500/20 border border-emerald-500 text-emerald-950 rounded-2xl text-xs font-bold flex items-center justify-between shadow-sm animate-fade-in">
          <div className="flex items-center gap-2">
            <Download className="w-4 h-4 text-emerald-700 animate-bounce" />
            <span>{downloadPillNotice}</span>
          </div>
          <span className="font-mono text-[10px] text-emerald-800">Direct CDN Binary Link Verified</span>
        </div>
      )}

      {/* ========================================================================= */}
      {/* 2. CLINICAL DISABILITY SPECIALTY BANNER (Clarvos Bento)                  */}
      {/* ========================================================================= */}
      <div className={`p-6 sm:p-8 rounded-[32px] border shadow-xl flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6 relative overflow-hidden ${
        highContrastMode ? 'bg-black border-[#ffd64e]' : 'bg-[#1c1b1f] text-white border-white/10'
      }`}>
        <div className="space-y-3 max-w-3xl">
          <div className="flex flex-wrap items-center gap-2">
            <span className="px-3.5 py-1 rounded-full bg-[#ffd64e] text-[#1c1b1f] font-black text-xs uppercase tracking-wider shadow">
              ⭐ Primary Disability Wing: {disabilityCluster.shortName}
            </span>
            <span className="px-3 py-1 rounded-full bg-white/10 text-white/80 font-mono text-[11px] border border-white/15">
              RPwD 2016: {disabilityCluster.rpwdAlignment}
            </span>
            <span className="px-3 py-1 rounded-full bg-white/10 text-white/80 font-mono text-[11px] border border-white/15">
              DSM-5: {disabilityCluster.dsmCode}
            </span>
          </div>

          <h1 className="text-2xl sm:text-3xl font-black text-white tracking-tight">
            {disabilityCluster.name}
          </h1>

          <p className="text-xs sm:text-sm text-white/70 leading-relaxed font-normal">
            {disabilityCluster.description} System is actively calibrated to <strong className="text-white">{activeStudent.name}</strong>'s sensory profile, showing strictly prescribed therapeutic exercises and accommodations.
          </p>

          <div className="flex flex-wrap items-center gap-4 pt-1 text-xs text-white/80">
            <div>Therapist: <strong className="text-[#ffd64e]">{activeStudent.therapist}</strong></div>
            <div>•</div>
            <div>IEP Status: <strong className="text-emerald-400 font-mono">{activeStudent.iepStatus}</strong></div>
            <div>•</div>
            <div>Active Goal Mastery: <strong className="text-[#ffd64e] font-mono">{activeStudent.overallMasteryPercent}%</strong></div>
          </div>
        </div>

        {/* Quick Launch Action Cluster */}
        <div className="flex flex-col sm:flex-row lg:flex-col items-stretch gap-2.5 shrink-0 w-full sm:w-auto">
          {onOpenAAC && (
            <button
              onClick={onOpenAAC}
              className="px-6 py-3.5 rounded-full bg-[#ffd64e] hover:bg-[#f5cb3a] text-[#1c1b1f] font-black text-xs uppercase tracking-wider transition-all shadow-lg flex items-center justify-center gap-2 cursor-pointer"
            >
              <Volume2 className="w-4 h-4" />
              <span>Launch AAC Speech Board</span>
            </button>
          )}

          <button
            onClick={toggleSensoryCalm}
            className={`px-6 py-3 rounded-full text-xs font-extrabold uppercase tracking-wider border transition-all flex items-center justify-center gap-2 cursor-pointer ${
              isSensoryCalmActive 
                ? 'bg-rose-600 text-white border-rose-500 shadow-lg' 
                : 'bg-white/10 hover:bg-white/20 text-white border-white/20'
            }`}
          >
            <Sparkles className="w-3.5 h-3.5 text-[#ffd64e]" />
            <span>{isSensoryCalmActive ? 'Stop Sensory Calm' : 'Sensory Snoezelen Break'}</span>
          </button>
        </div>
      </div>

      {/* ========================================================================= */}
      {/* 3. QUICK AAC COMMUNICATION STRIP (Instant Voice for Non-Verbal Students) */}
      {/* ========================================================================= */}
      <div className={`p-4 rounded-[28px] border shadow-sm space-y-2 ${
        highContrastMode ? 'bg-[#121212] border-[#ffd64e]' : 'bg-[#eae7e1]/50 border-black/10'
      }`}>
        <div className="flex items-center justify-between text-xs px-1">
          <span className="font-extrabold uppercase tracking-wider text-[#1c1b1f]/70 flex items-center gap-1.5">
            <Volume2 className="w-3.5 h-3.5 text-[#d97706]" />
            <span>Quick-Tap Assistive Voice Prompts</span>
          </span>
          <span className="text-[10px] text-[#1c1b1f]/50 font-mono">Instant Voice Synthesizer</span>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2">
          {[
            { label: 'I Need Help', emoji: '🙋‍♂️', phrase: 'I need assistance please.' },
            { label: 'Yes', emoji: '✅', phrase: 'Yes.' },
            { label: 'No', emoji: '❌', phrase: 'No thank you.' },
            { label: 'Water Please', emoji: '💧', phrase: 'May I have some water please?' },
            { label: 'Bathroom', emoji: '🚻', phrase: 'I need to use the washroom.' },
            { label: 'Take a Break', emoji: '⏸️', phrase: 'I need a sensory quiet break.' },
          ].map((item, idx) => (
            <button
              key={idx}
              onClick={() => triggerQuickAAC(item.phrase)}
              className="px-3 py-2.5 rounded-2xl bg-white hover:bg-[#ffd64e] text-[#1c1b1f] border border-black/10 font-black text-xs flex items-center justify-center gap-2 shadow-xs hover:shadow-md transition-all transform active:scale-95 cursor-pointer"
            >
              <span className="text-base">{item.emoji}</span>
              <span className="truncate">{item.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* ========================================================================= */}
      {/* 3.5. FIRST-THEN VISUAL SCHEDULE CARD (Standard Autism & ADHD Ergonomic)   */}
      {/* ========================================================================= */}
      <div className="bg-[#ffd64e] p-4 sm:p-5 rounded-[28px] border border-black/10 shadow-md text-[#1c1b1f] flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4">
        <div className="flex items-center gap-2 font-black text-xs uppercase tracking-wider">
          <span className="p-1.5 rounded-xl bg-[#1c1b1f] text-[#ffd64e]">📋</span>
          <span>First-Then Visual Structure:</span>
        </div>

        <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {/* First Box */}
          <div className="bg-white p-3.5 rounded-2xl border border-black/10 flex items-center gap-3 shadow-xs">
            <span className="w-8 h-8 rounded-xl bg-amber-100 text-amber-900 font-black text-xs flex items-center justify-center shrink-0">
              1ST
            </span>
            <div>
              <div className="text-[10px] font-mono font-bold uppercase text-slate-500">First Action</div>
              <div className="text-xs font-black text-slate-900 flex items-center gap-1.5">
                <span>🧘</span>
                <span>Sensory Snoezelen Calm Break</span>
              </div>
            </div>
          </div>

          {/* Then Box */}
          <div className="bg-white p-3.5 rounded-2xl border border-black/10 flex items-center gap-3 shadow-xs">
            <span className="w-8 h-8 rounded-xl bg-emerald-100 text-emerald-900 font-black text-xs flex items-center justify-center shrink-0">
              THEN
            </span>
            <div>
              <div className="text-[10px] font-mono font-bold uppercase text-slate-500">Next Activity</div>
              <div className="text-xs font-black text-slate-900 flex items-center gap-1.5">
                <span>🗣️</span>
                <span>AAC Speech Matrix Practice</span>
              </div>
            </div>
          </div>
        </div>

        <button
          onClick={toggleSensoryCalm}
          className={`px-5 py-3 rounded-full text-xs font-black uppercase tracking-wider transition-all flex items-center justify-center gap-2 shadow-sm cursor-pointer shrink-0 ${
            isSensoryCalmActive 
              ? 'bg-rose-600 text-white animate-pulse' 
              : 'bg-[#1c1b1f] hover:bg-black text-[#ffd64e]'
          }`}
        >
          <Sparkles className="w-4 h-4 text-[#ffd64e]" />
          <span>{isSensoryCalmActive ? 'Calm Mode Active' : 'One-Tap Sensory Calm'}</span>
        </button>
      </div>

      {/* ========================================================================= */}
      {/* 4. CLARVOS TAB NAVIGATION BAR                                            */}
      {/* ========================================================================= */}
      <div className="flex flex-wrap items-center gap-2 border-b border-black/10 pb-3">
        {[
          { id: 'apps', label: `My Prescribed Apps (${prescribedApps.length})`, icon: Brain },
          { id: 'goals', label: 'My IEP Learning Goals', icon: GraduationCap },
          { id: 'timetable', label: 'Visual Schedule', icon: Calendar },
          { id: 'accommodations', label: 'Sensory & Motor Accommodations', icon: Sliders },
          { id: 'mood', label: 'Daily Mood Check-in', icon: Smile },
          { id: 'rewards', label: `Token Stars (${earnedStars} ⭐)`, icon: Trophy },
        ].map((tab) => {
          const Icon = tab.icon;
          const isSelected = activeTab === tab.id;

          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`px-5 py-2.5 rounded-full text-xs font-black transition-all flex items-center gap-2 cursor-pointer ${
                isSelected
                  ? 'bg-[#1c1b1f] text-[#ffd64e] shadow-md scale-[1.02]'
                  : 'bg-white text-slate-700 hover:bg-slate-100 border border-black/10'
              }`}
            >
              <Icon className="w-4 h-4 text-current" />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* ========================================================================= */}
      {/* TAB 1: PRESCRIBED APPS ONLY (Condition-Specific Apps)                    */}
      {/* ========================================================================= */}
      {activeTab === 'apps' && (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <span className="text-[10px] font-black tracking-wider uppercase px-3 py-1 rounded-full bg-[#ffd64e]/20 text-slate-900 border border-[#ffd64e]/40">
                Disability-Filtered Curriculum
              </span>
              <h2 className="text-xl sm:text-2xl font-black text-[#1c1b1f] mt-1.5">
                Clinical Modules Prescribed for {activeStudent.name}
              </h2>
              <p className="text-xs text-slate-600">
                Unrelated applications have been filtered out. Prescribed for <strong className="text-black">{disabilityCluster.name}</strong>
                {activeStudent.coOccurringDisabilityCategory && (
                  <span className="font-semibold text-purple-700"> and {activeStudent.coOccurringDisabilityCategory}</span>
                )}.
              </p>
            </div>

            <button
              onClick={() => speakText(`You have ${prescribedApps.length} prescribed modules available for ${disabilityCluster.shortName}: ${prescribedApps.map(a => a.name).join(', ')}.`)}
              className="px-4 py-2 bg-[#1c1b1f] text-[#ffd64e] rounded-full text-xs font-black flex items-center gap-2 hover:bg-black transition-all shadow-sm cursor-pointer self-start sm:self-auto"
            >
              <Volume2 className="w-4 h-4 text-[#ffd64e]" /> Listen to App List
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {prescribedApps.map((app, idx) => {
              const isPrimary = disabilityCluster.primaryModuleIds.includes(app.id);
              const isCoOccurring = coOccurringCluster?.primaryModuleIds.includes(app.id);

              return (
                <div 
                  key={app.id} 
                  className="bg-white rounded-[28px] border border-black/10 p-6 shadow-sm hover:shadow-xl hover:border-black/20 transition-all duration-300 flex flex-col justify-between space-y-4 group"
                >
                  <div className="space-y-3">
                    {/* Header Pill & Icon */}
                    <div className="flex items-center justify-between">
                      <div className="w-12 h-12 rounded-2xl bg-[#f9f6f0] border border-black/10 flex items-center justify-center text-2xl group-hover:scale-110 transition-transform">
                        {app.icon}
                      </div>
                      <span className={`px-2.5 py-1 rounded-full text-[10px] font-black tracking-wider uppercase ${
                        isPrimary 
                          ? 'bg-[#ffd64e] text-black border border-black/20' 
                          : isCoOccurring
                            ? 'bg-purple-100 text-purple-800 border border-purple-300'
                            : 'bg-slate-100 text-slate-700'
                      }`}>
                        {isPrimary 
                          ? '⭐ Primary Tool' 
                          : isCoOccurring
                            ? '✨ Dual Diagnosis Tool'
                            : 'Support Tool'}
                      </span>
                    </div>

                    <div>
                      <div className="text-[10px] font-mono uppercase text-slate-500 font-bold">{app.category}</div>
                      <h3 className="text-base font-black text-[#1c1b1f] group-hover:text-amber-700 transition-colors">
                        {app.name}
                      </h3>
                    </div>

                    <p className="text-xs text-slate-600 leading-relaxed">
                      {app.clinicalPurpose}
                    </p>

                    <div className="p-2.5 rounded-xl bg-[#f9f6f0] border border-black/5 text-[11px] text-slate-700 font-medium">
                      🎯 <strong className="text-slate-900">Clinical Target:</strong> {app.evidenceTarget}
                    </div>

                    {/* Mastery Progress Bar */}
                    <div className="space-y-1 pt-1">
                      <div className="flex justify-between text-xs font-black">
                        <span className="text-slate-500">Mastery Progress</span>
                        <span className="font-mono text-emerald-700">{app.defaultMastery}%</span>
                      </div>
                      <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden">
                        <div 
                          className="bg-[#ffd64e] h-full rounded-full transition-all duration-700"
                          style={{ width: `${app.defaultMastery}%` }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons: 1-Click Launch & Download Pills */}
                  <div className="space-y-2 pt-2 border-t border-black/5">
                    <button
                      onClick={() => {
                        if (onSelectModule) onSelectModule(app.id);
                        else speakText(`Launching ${app.name}...`);
                      }}
                      className="w-full py-2.5 rounded-full bg-[#1c1b1f] hover:bg-black text-[#ffd64e] font-black text-xs flex items-center justify-center gap-2 shadow-sm transition-all cursor-pointer"
                    >
                      <Play className="w-3.5 h-3.5 fill-current" />
                      <span>Launch App in Browser</span>
                    </button>

                    {/* Native App Download Pills */}
                    <div className="flex items-center justify-center gap-1.5 pt-1">
                      {app.platforms.includes('android') && (
                        <button
                          onClick={() => handleDownloadApp(app, 'android')}
                          className="px-2.5 py-1 rounded-full bg-[#f9f6f0] hover:bg-[#ffd64e] text-slate-800 text-[10px] font-bold border border-black/10 transition cursor-pointer flex items-center gap-1"
                          title="Download Android APK"
                        >
                          <Smartphone className="w-3 h-3 text-emerald-600" />
                          <span>.APK</span>
                        </button>
                      )}
                      {app.platforms.includes('windows') && (
                        <button
                          onClick={() => handleDownloadApp(app, 'windows')}
                          className="px-2.5 py-1 rounded-full bg-[#f9f6f0] hover:bg-[#ffd64e] text-slate-800 text-[10px] font-bold border border-black/10 transition cursor-pointer flex items-center gap-1"
                          title="Download Windows EXE"
                        >
                          <Laptop className="w-3 h-3 text-sky-600" />
                          <span>.EXE</span>
                        </button>
                      )}
                      {app.platforms.includes('macos') && (
                        <button
                          onClick={() => handleDownloadApp(app, 'macos')}
                          className="px-2.5 py-1 rounded-full bg-[#f9f6f0] hover:bg-[#ffd64e] text-slate-800 text-[10px] font-bold border border-black/10 transition cursor-pointer flex items-center gap-1"
                          title="Download macOS DMG"
                        >
                          <Laptop className="w-3 h-3 text-purple-600" />
                          <span>.DMG</span>
                        </button>
                      )}
                      <button
                        onClick={() => handleDownloadApp(app, 'pwa')}
                        className="px-2.5 py-1 rounded-full bg-[#f9f6f0] hover:bg-[#ffd64e] text-slate-800 text-[10px] font-bold border border-black/10 transition cursor-pointer flex items-center gap-1"
                        title="Install Progressive Web App (PWA)"
                      >
                        <Download className="w-3 h-3 text-amber-600" />
                        <span>PWA</span>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* TAB 2: MY IEP LEARNING GOALS                                             */}
      {/* ========================================================================= */}
      {activeTab === 'goals' && (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <span className="text-[10px] font-black tracking-wider uppercase px-2.5 py-1 rounded-full bg-[#ffd64e]/20 text-slate-900 border border-[#ffd64e]/40">
                Active IEP Curriculum
              </span>
              <h2 className="text-xl font-black text-[#1c1b1f] mt-1.5">
                Current SMART IEP Goals for {activeStudent.name}
              </h2>
            </div>
            <button
              onClick={() => speakText(`Your IEP goals for today are: ${goals.map(g => `${g.title}, progress ${g.progress} percent`).join('. ')}`)}
              className="px-4 py-2 bg-[#1c1b1f] text-[#ffd64e] rounded-full text-xs font-black flex items-center gap-2 hover:bg-black transition-all shadow-sm cursor-pointer self-start sm:self-auto"
            >
              <Volume2 className="w-4 h-4 text-[#ffd64e]" /> Listen to Goals
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {goals.map((g, idx) => (
              <div key={idx} className="bg-white p-6 rounded-[28px] border border-[#1c1b1f]/10 shadow-sm hover:shadow-xl transition-all duration-300 space-y-4 relative flex flex-col justify-between group">
                <div className="space-y-3">
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-extrabold text-[#1c1b1f]/60 uppercase tracking-wider text-[10px] bg-slate-100 px-2.5 py-1 rounded-full">
                      {g.category}
                    </span>
                    <span className="font-black px-2.5 py-1 bg-emerald-100 text-emerald-900 border border-emerald-300 rounded-full text-[10px]">
                      {g.status}
                    </span>
                  </div>

                  <h3 className="font-black text-[#1c1b1f] text-base leading-snug group-hover:text-amber-600 transition-colors">
                    {g.title}
                  </h3>

                  <div className="space-y-1.5 pt-2">
                    <div className="flex justify-between text-xs font-black">
                      <span className="text-[#1c1b1f]/60">Mastery Progress</span>
                      <span className="text-[#1c1b1f] font-mono">{g.progress}%</span>
                    </div>
                    <div className="w-full bg-slate-100 h-2.5 rounded-full overflow-hidden p-0.5">
                      <div className="bg-[#ffd64e] h-full rounded-full transition-all duration-700 shadow-sm" style={{ width: `${g.progress}%` }} />
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => speakText(`Goal ${idx + 1}: ${g.title}. Current progress is ${g.progress} percent.`)}
                  className="w-full py-2.5 bg-slate-100 hover:bg-[#ffd64e] hover:text-[#1c1b1f] text-slate-700 font-black text-xs rounded-full flex items-center justify-center gap-2 border border-slate-200 transition-all cursor-pointer mt-4"
                >
                  <Volume2 className="w-3.5 h-3.5" /> Read Goal Aloud
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* TAB 3: VISUAL SCHEDULE WITH COMPLETION CHECKS                            */}
      {/* ========================================================================= */}
      {activeTab === 'timetable' && (
        <div className="bg-white p-8 rounded-[32px] border border-[#1c1b1f]/10 shadow-sm space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <span className="text-[10px] font-black tracking-wider uppercase px-2.5 py-1 rounded-full bg-[#ffd64e]/20 text-slate-900 border border-[#ffd64e]/40">
                Visual Schedule Matrix
              </span>
              <h2 className="text-xl font-black text-[#1c1b1f] mt-1.5">Today's Schedule for {activeStudent.name}</h2>
              <p className="text-xs text-slate-600">Click checkmark to mark task complete and earn bonus Token Stars.</p>
            </div>
            <button
              onClick={() => speakText(`Your schedule for today: ${timetable.map(t => `${t.time}, ${t.subject} with ${t.teacher} in ${t.room}`).join('. ')}`)}
              className="px-4 py-2 bg-[#1c1b1f] text-[#ffd64e] rounded-full text-xs font-black flex items-center gap-2 hover:bg-black transition-all shadow-sm cursor-pointer self-start sm:self-auto"
            >
              <Volume2 className="w-4 h-4 text-[#ffd64e]" /> Read Schedule Aloud
            </button>
          </div>

          <div className="space-y-3.5">
            {timetable.map((t) => {
              const isDone = completedTasks.includes(t.id);

              return (
                <div 
                  key={t.id} 
                  className={`p-5 rounded-[24px] border flex flex-col sm:flex-row sm:items-center justify-between gap-4 transition-all duration-300 ${
                    isDone 
                      ? 'bg-emerald-500/10 border-emerald-400' 
                      : 'bg-[#f9f6f0] border-black/10 hover:border-[#ffd64e]'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-white border border-black/10 flex items-center justify-center text-2xl shadow-xs shrink-0">
                      {t.icon}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="px-2.5 py-0.5 rounded-full bg-[#1c1b1f] text-[#ffd64e] font-mono text-[10px] font-black">
                          {t.time}
                        </span>
                        {isDone && (
                          <span className="px-2.5 py-0.5 rounded-full bg-emerald-200 text-emerald-900 text-[10px] font-extrabold">
                            Completed ✓
                          </span>
                        )}
                      </div>
                      <div className="font-black text-[#1c1b1f] text-sm mt-1">{t.subject}</div>
                      <div className="text-xs text-slate-600">Educator: <strong className="text-black">{t.teacher}</strong> • Room: <strong className="text-black">{t.room}</strong></div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 self-start sm:self-auto">
                    <button
                      onClick={() => toggleTaskCompletion(t.id)}
                      className={`px-4 py-2 rounded-full font-black text-xs flex items-center gap-1.5 transition-all cursor-pointer ${
                        isDone 
                          ? 'bg-emerald-600 text-white shadow' 
                          : 'bg-white hover:bg-[#ffd64e] text-black border border-black/10'
                      }`}
                    >
                      <Check className="w-3.5 h-3.5" />
                      <span>{isDone ? 'Mark Undone' : 'Complete Task (+1 ⭐)'}</span>
                    </button>

                    <button
                      onClick={() => speakText(`At ${t.time}, ${t.subject} with ${t.teacher} in ${t.room}`)}
                      className="p-2.5 bg-white hover:bg-[#ffd64e] text-[#1c1b1f] rounded-full border border-black/10 shadow-xs transition-colors cursor-pointer"
                      title="Read Class Aloud"
                    >
                      <Volume2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* TAB 4: SENSORY & MOTOR ACCOMMODATIONS SIMULATOR                           */}
      {/* ========================================================================= */}
      {activeTab === 'accommodations' && (
        <div className="space-y-6">
          <div className="bg-white p-8 rounded-[32px] border border-black/10 shadow-sm flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="space-y-1">
              <span className="px-3 py-1 bg-[#1c1b1f] text-[#ffd64e] text-xs font-black rounded-full uppercase tracking-wider">
                Clinical Assistive Profile & Sensor Tuning
              </span>
              <h2 className="text-2xl font-black text-[#1c1b1f]">
                Assistive Accommodations for {activeStudent.name}
              </h2>
              <p className="text-xs text-slate-600">
                Configured according to <strong className="text-black">{activeStudent.disabilityCategory}</strong> ({disabilityCluster.rpwdAlignment})
                {activeStudent.coOccurringDisabilityCategory && (
                  <span className="inline-block ml-2 px-2 py-0.5 bg-purple-100 text-purple-800 border border-purple-300 rounded-full font-bold text-[10px]">
                    + Dual: {activeStudent.coOccurringDisabilityCategory}
                  </span>
                )}
              </p>
            </div>

            <button
              onClick={() => speakText(`Accommodations profile for ${activeStudent.name}. Diagnosis category is ${disabilityCluster.name}. Active accommodations include: ${disabilityCluster.accommodations.join(', ')}. Calibrated dwell is ${dwellSliderMs} milliseconds.`)}
              className="px-5 py-2.5 bg-[#1c1b1f] hover:bg-black text-[#ffd64e] font-black rounded-full text-xs flex items-center gap-2 shadow-sm shrink-0 cursor-pointer"
            >
              <Volume2 className="w-4 h-4 text-[#ffd64e]" />
              <span>Listen to Assistive Profile</span>
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            
            {/* Column 1: Active Accommodations Checklist */}
            <div className="bg-white p-6 rounded-[28px] border border-black/10 shadow-sm space-y-4">
              <div className="flex items-center gap-2 text-[#1c1b1f] font-black text-sm">
                <Sparkles className="w-5 h-5 text-[#ffd64e]" />
                <h3>Active Clinical Accommodations</h3>
              </div>
              <div className="space-y-2">
                {disabilityCluster.accommodations.map((acc, aidx) => (
                  <div key={aidx} className="p-3 bg-emerald-500/10 rounded-2xl border border-emerald-300 flex items-center gap-2 text-xs font-bold text-emerald-950">
                    <CheckCircle2 className="w-4 h-4 text-emerald-700 shrink-0" />
                    <span>{acc}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Column 2: Motor Dwell & Switch Access Simulator */}
            <div className="bg-white p-6 rounded-[28px] border border-black/10 shadow-sm space-y-4">
              <div className="flex items-center gap-2 text-[#1c1b1f] font-black text-sm">
                <Activity className="w-5 h-5 text-emerald-600" />
                <h3>Motor Dwell Switch Simulator</h3>
              </div>
              <p className="text-xs text-slate-600 leading-relaxed">
                Calibrate fixation dwell latency for students with Cerebral Palsy, tremors, or eye-gaze control.
              </p>

              <div className="space-y-2 pt-2">
                <div className="flex justify-between text-xs font-mono font-bold">
                  <span>Dwell Sensitivity:</span>
                  <span className="text-black font-black">{dwellSliderMs} ms</span>
                </div>
                <input
                  type="range"
                  min="200"
                  max="1500"
                  step="50"
                  value={dwellSliderMs}
                  onChange={(e) => setDwellSliderMs(Number(e.target.value))}
                  className="w-full accent-[#ffd64e] cursor-pointer"
                />
              </div>

              {/* Live Test Target Button */}
              <button
                onClick={handleDwellTrigger}
                disabled={isDwellSimulating}
                className="w-full py-4 rounded-2xl bg-[#1c1b1f] hover:bg-black text-white font-black text-xs relative overflow-hidden transition-all shadow-md cursor-pointer"
              >
                {isDwellSimulating && (
                  <div 
                    className="absolute inset-0 bg-[#ffd64e]/40 transition-all"
                    style={{ width: `${Math.max(0, 100 - ((dwellCountdown || 0) / dwellSliderMs) * 100)}%` }}
                  />
                )}
                <span className="relative z-10">
                  {isDwellSimulating 
                    ? `DWELLING... ${dwellCountdown}ms` 
                    : '👉 Tap & Hold to Test Dwell Activation'}
                </span>
              </button>
            </div>

            {/* Column 3: Sensory Room & Auditory Presets */}
            <div className="bg-white p-6 rounded-[28px] border border-black/10 shadow-sm space-y-4">
              <div className="flex items-center gap-2 text-[#1c1b1f] font-black text-sm">
                <Sun className="w-5 h-5 text-amber-500" />
                <h3>Sensory Environment Presets</h3>
              </div>
              <div className="p-4 rounded-2xl bg-[#f9f6f0] border border-black/5 text-xs space-y-2 text-slate-700">
                <div className="font-bold text-black">Active Ambient Preset:</div>
                <div className="font-mono text-emerald-800 font-extrabold">
                  {disabilityCluster.sensoryPreset.ambientSoundscape || 'Dynamic Calming Mode'}
                </div>
                <p className="text-[11px] text-slate-500">
                  Acoustic frequencies are calibrated to prevent sensory overload and reduce physiological stress markers.
                </p>
              </div>

              <button
                onClick={toggleSensoryCalm}
                className="w-full py-3 bg-[#ffd64e] hover:bg-[#f5cb3a] text-[#1c1b1f] font-black rounded-full text-xs shadow-md transition-all flex items-center justify-center gap-2 cursor-pointer"
              >
                <Music className="w-4 h-4" />
                <span>{isSensoryCalmActive ? 'Stop Ambient Audio' : 'Play Ambient Soundscape'}</span>
              </button>
            </div>

          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* TAB 5: DAILY MOOD & SENSORY CHECK-IN                                     */}
      {/* ========================================================================= */}
      {activeTab === 'mood' && (
        <div className="bg-white p-8 rounded-[32px] border border-black/10 shadow-sm space-y-6">
          <div>
            <span className="px-3 py-1 bg-[#1c1b1f] text-[#ffd64e] text-xs font-black rounded-full uppercase tracking-wider">
              Emotional Regulation & Energy Check
            </span>
            <h2 className="text-2xl font-black text-[#1c1b1f] mt-3">
              How are you feeling right now, {activeStudent.name}?
            </h2>
            <p className="text-xs text-slate-600 mt-1">
              Select your current emotional state to auto-adjust ambient soundscapes and lesson pacing.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
            {moods.map((m, idx) => {
              const isSelected = selectedMood === m.label;

              return (
                <button
                  key={idx}
                  onClick={() => setSelectedMood(m.label)}
                  className={`p-6 rounded-[28px] border text-left space-y-3 transition-all duration-300 transform active:scale-95 cursor-pointer ${
                    isSelected 
                      ? 'bg-[#1c1b1f] text-white border-[#ffd64e] shadow-xl ring-4 ring-[#ffd64e]/30' 
                      : 'bg-[#f9f6f0] text-slate-900 border-black/10 hover:border-[#ffd64e] hover:bg-white'
                  }`}
                >
                  <div className="text-4xl">{m.emoji}</div>
                  <div>
                    <div className="font-black text-sm">{m.label}</div>
                    <div className={`text-[11px] mt-1 font-medium ${isSelected ? 'text-slate-300' : 'text-slate-600'}`}>
                      {m.sensoryRec}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {selectedMood && (
            <div className="p-5 bg-[#ffd64e]/15 border border-[#ffd64e]/40 rounded-[24px] flex flex-col sm:flex-row items-center justify-between text-xs gap-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#ffd64e] text-[#1c1b1f] flex items-center justify-center font-bold shrink-0">
                  <Sparkles className="w-5 h-5 text-[#1c1b1f]" />
                </div>
                <div>
                  <div className="font-black text-[#1c1b1f] text-sm">Active Mood Logged: {selectedMood}</div>
                  <div className="text-slate-700">Sensory pacing and therapist notifications updated automatically.</div>
                </div>
              </div>
              <button
                onClick={() => speakText(`Your mood is recorded as ${selectedMood}. Pacing adjusted.`)}
                className="px-4 py-2 bg-[#1c1b1f] text-[#ffd64e] font-black rounded-full shadow text-xs flex items-center gap-2 cursor-pointer shrink-0"
              >
                <Volume2 className="w-3.5 h-3.5" /> Read Aloud
              </button>
            </div>
          )}
        </div>
      )}

      {/* ========================================================================= */}
      {/* TAB 6: TOKEN STAR REWARD BOARD                                           */}
      {/* ========================================================================= */}
      {activeTab === 'rewards' && (
        <div className="bg-[#1c1b1f] text-white p-10 rounded-[32px] border border-white/10 shadow-2xl text-center space-y-6 relative overflow-hidden">
          <div className="w-20 h-20 bg-[#ffd64e] text-black border-4 border-amber-300 rounded-full flex items-center justify-center mx-auto shadow-2xl text-4xl animate-bounce">
            ⭐
          </div>

          <div className="space-y-2">
            <span className="text-[11px] font-black tracking-widest uppercase px-3 py-1 rounded-full bg-white/10 text-[#ffd64e] border border-white/10">
              Positive Behavior Reinforcement
            </span>
            <h2 className="text-3xl font-black text-white">Token Star Reward Bank</h2>
            <p className="text-xs text-slate-300 max-w-md mx-auto font-medium">
              Outstanding work, <strong className="text-[#ffd64e] font-black">{activeStudent.name}</strong>! You have earned {earnedStars} Token Stars this week for completing clinical exercises!
            </p>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-3 py-3">
            {[...Array(Math.min(10, earnedStars))].map((_, i) => (
              <div key={i} className="w-14 h-14 bg-white/5 border border-[#ffd64e]/40 rounded-2xl flex items-center justify-center text-3xl shadow-lg hover:scale-110 hover:border-[#ffd64e] transition-all">
                ⭐
              </div>
            ))}
          </div>

          <div className="flex flex-wrap items-center justify-center gap-3 pt-2">
            <button
              onClick={() => {
                speakText(`Congratulations ${activeStudent.name}! You have earned ${earnedStars} Token Stars! You are doing amazing!`);
                audioEngine.playCelebrationChime();
              }}
              className="px-6 py-3 bg-[#ffd64e] text-black font-black text-xs rounded-full shadow-lg hover:bg-amber-400 transition-all flex items-center gap-2 cursor-pointer"
            >
              <Volume2 className="w-4 h-4 text-black" /> Hear Celebration
            </button>

            <button
              onClick={() => alert(`Certificate of Achievement generated for ${activeStudent.name} (${disabilityCluster.shortName} Achievement)!`)}
              className="px-6 py-3 bg-white/10 text-white font-black text-xs rounded-full border border-white/20 hover:bg-white hover:text-black transition-all flex items-center gap-2 cursor-pointer"
            >
              <Award className="w-4 h-4 text-[#ffd64e]" /> Print Achievement Certificate
            </button>
          </div>
        </div>
      )}

    </div>
  );
};
